import * as React from 'react'
import { PropsWithChildren } from 'react'
import classnames from 'classnames'
import { areTimelinesEqual, isTimeline } from '../../../../utils/date'
import { Option } from '../types'
import { useUniqueOptions } from '../../../../hooks/useUniqueOptions/useUniqueOptions'

export type RadioGroupProps = {
    options?: Array<Option>
    value: Option['value']
    onChange: (value: Option['value']) => void
    name: string
    wrapperClassname?: string
    itemWrapperClassname?: string
    radioClassName?: string
    labelClassname?: string
    disabled?: boolean
    renderLabel?: (option: Option) => React.ReactNode
}

export const RadioGroup: React.FC<RadioGroupProps> = (props) => {
    const {
        options,
        value,
        onChange,
        name,
        wrapperClassname,
        disabled,
        renderLabel,
    } = props

    // Deduplicate options by value, keeping the first occurrence
    const uniqueOptions = useUniqueOptions(options)

    return (
        <fieldset
            className={wrapperClassname}
            role="radiogroup"
            data-testid={`${name}-radio-group`}
            disabled={disabled}
        >
            {uniqueOptions?.map((option) => {
                const inputValue =
                    typeof option.value === 'string'
                        ? option.value
                        : JSON.stringify(option.value)
                const inputChecked =
                    isTimeline(option.value) && isTimeline(value)
                        ? areTimelinesEqual(option.value, value)
                        : option.value === value

                return (
                    <RadioGroupItem
                        key={inputValue}
                        itemWrapperClassname={props.itemWrapperClassname}
                        radioClassName={props.radioClassName}
                        name={name}
                        value={inputValue}
                        checked={inputChecked}
                        disabled={!!option.disabled}
                        labelClassname={props.labelClassname}
                        onChange={() => {
                            onChange(option.value)
                        }}
                    >
                        {renderLabel ? renderLabel(option) : option.label}
                    </RadioGroupItem>
                )
            })}
        </fieldset>
    )
}

type RadioGroupItemProps = PropsWithChildren & {
    name: string
    value: string
    onChange: () => void
    checked?: boolean
    disabled?: boolean
    itemWrapperClassname?: string
    labelClassname?: string
    radioClassName?: string
    rawLabel?: string
}

export const RadioGroupItem: React.FC<RadioGroupItemProps> = (props) => {
    const { name, value, checked, disabled, onChange, children } = props

    const itemWrapperClassname = classnames(
        // necessary for properly positioning input element and not cause position or scroll issues
        // use of sr-only instead of hidden allows for focus and tab navigation
        'relative',
        'group/label flex items-center gap-2',
        'hover:bg-blue-50',
        'active:bg-blue-100',
        'cursor-pointer',
        props.itemWrapperClassname
    )

    const labelClassname = classnames(
        'text-label-small text-regular peer-disabled:text-disabled',
        {
            'text-disabled opacity-50': disabled,
        },
        props.labelClassname
    )

    const fakeRadioClassName = classnames(
        'relative h-4 w-4 rounded-full border text-neutral-300 bg-white',
        'outline outline-0 outline-white/0',
        'group-active/label:border-neutral-500',
        'shrink-0',

        // checked
        'peer-checked:[&>div]:block',
        'peer-checked:text-azure',

        // focus
        'peer-focus:outline peer-focus:outline-4 peer-focus:outline-azure/35 peer-focus:border-neutral-400',
        // focus - checked
        'peer-focus:peer-checked:border-azure',

        // hover
        'group-hover/label:border-neutral-400',
        // hover - checked
        'group-hover/label:peer-checked:text-blue-600',
        'group-hover/label:peer-checked:border-blue-600',
        // hover - active
        'group-hover/label:peer-active:text-blue-700',
        'group-hover/label:peer-active:border-blue-700',

        // disabled
        'peer-disabled:!text-disabled',
        'peer-disabled:!border-disabled',
        props.radioClassName
    )

    const title =
        props.rawLabel ?? (typeof children === 'string' ? children : undefined)

    return (
        <label className={itemWrapperClassname} title={title}>
            <input
                type="radio"
                name={name}
                value={value}
                data-testid={`${name}-radio-group-${value}`}
                checked={checked}
                className="peer hidden"
                disabled={disabled}
                onChange={onChange}
            />
            <div className={fakeRadioClassName}>
                <div className="absolute left-1/2 top-1/2 hidden h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[currentColor]"></div>
            </div>
            <div className={labelClassname}>{children}</div>
        </label>
    )
}
